// Generated from legacy db.ts to keep files under 500 lines.
import { sql } from "../../base";
import type { TaskTemplate } from "../../types";

const taskTemplatesModule = {
  async findByGoalTemplateId(goalTemplateId: string): Promise<TaskTemplate[]> {
    try {
      const result = await sql`
        SELECT * FROM task_templates
        WHERE goal_template_id = ${goalTemplateId}
        ORDER BY position ASC
      `;
      return (result || []) as TaskTemplate[];
    } catch (error) {
      console.error('Error fetching task templates:', error);
      return [];
    }
  },
  
  async create(data: Omit<TaskTemplate, 'id' | 'created_at' | 'updated_at'>): Promise<TaskTemplate> {
    const result = await sql`
      INSERT INTO task_templates (
        goal_template_id, title, description, priority, position, user_id,
        depends_on_positions, dependency_type, dependency_group,
        offset_from_position, offset_days, offset_type
      )
      VALUES (
        ${data.goal_template_id}, ${data.title}, ${data.description}, 
        ${data.priority}, ${data.position}, ${data.user_id},
        ${data.depends_on_positions || []}, ${data.dependency_type || 'none'}, ${data.dependency_group},
        ${data.offset_from_position}, ${data.offset_days || 0}, ${data.offset_type || 'none'}
      )
      RETURNING *
    `;
    return result[0] as TaskTemplate;
  },
  
  async update(id: string, userId: string, data: Partial<TaskTemplate>): Promise<TaskTemplate> {
    const result = await sql`
      UPDATE task_templates
      SET title = COALESCE(${data.title}, title),
          description = COALESCE(${data.description}, description),
          priority = COALESCE(${data.priority}, priority),
          position = COALESCE(${data.position}, position),
          depends_on_positions = COALESCE(${data.depends_on_positions}, depends_on_positions),
          dependency_type = COALESCE(${data.dependency_type}, dependency_type),
          dependency_group = COALESCE(${data.dependency_group}, dependency_group),
          offset_from_position = COALESCE(${data.offset_from_position}, offset_from_position),
          offset_days = COALESCE(${data.offset_days}, offset_days),
          offset_type = COALESCE(${data.offset_type}, offset_type),
          updated_at = now()
      WHERE id = ${id} AND user_id = ${userId}
      RETURNING *
    `;
    return result[0] as TaskTemplate;
  },
  
  async delete(id: string, userId: string): Promise<boolean> {
    const result = await sql`
      DELETE FROM task_templates
      WHERE id = ${id} AND user_id = ${userId}
    `;
    return result.count > 0;
  },
  
  async deleteByGoalTemplateId(goalTemplateId: string, userId: string): Promise<boolean> {
    // First check if the user owns the goal template
    const goalTemplate = await sql`
      SELECT id, user_id, is_system 
      FROM goal_templates 
      WHERE id = ${goalTemplateId}
    `;
    
    if (goalTemplate.length === 0) {
      return false; // Goal template doesn't exist
    }
    
    const template = goalTemplate[0];
    
    // If it's a system template, don't allow deletion
    if (template.is_system) {
      return false;
    }
    
    // If the user doesn't own the goal template, don't allow deletion
    if (template.user_id !== userId) {
      return false;
    }
    
    // If the user owns the goal template, remove any task templates tied to it.
    // We allow deleting rows where user_id is blank so older data can be cleaned up safely.
    const result = await sql`
      DELETE FROM task_templates
      WHERE goal_template_id = ${goalTemplateId}
        AND (user_id = ${userId} OR user_id IS NULL)
    `;
    return result.count > 0;
  }
};

export type TaskTemplatesModule = typeof taskTemplatesModule;

export function registerTaskTemplates(db: { taskTemplates?: TaskTemplatesModule }) {
  db.taskTemplates = taskTemplatesModule;
}

export { taskTemplatesModule as taskTemplates };
